import { useRouter } from 'next/router';
import queryString from 'query-string';
import { ParsedUrlQuery } from 'querystring';

import stringifyUrl from '../helpers/stringifyUrl';
import omit from '../utils/omit';

const getOptionalPathToMmb = (
  existingQuery: ParsedUrlQuery,
  newQueryParameters: ParsedUrlQuery,
  asPath: string,
  bookingId: number | null,
): string | null => {
  const { bid, language } = existingQuery;

  if (!bookingId && !bid) return null;

  const pathOfMmb2 = queryString.parseUrl(asPath);
  const apiBaseOfMmb2 = (pathOfMmb2.query.api_base as string) || '';

  const pathOfMmb1 = {
    // bookingId used in the trip companion
    // where the bid isn't available in query params or the path
    url: `/${language as string}/manage/${(bookingId ?? bid) as string}`,
    query: {
      ...omit(pathOfMmb2.query, [
        'api_base', // `api_base_*` not needed in MMB 1 (it uses `node_override_api*Url` instead).
        'api_base_payments',
        'help', // TODO: remove the omission of help param once helpcenter fixes this issue: https://jira.kiwi.com/browse/SFAQ-1379
        'days', // used in success page of refund offers
        'years', // used in success page of refund offers
        'is_final', // used in success page of refund offers
        'is_partial', // used in success page of refund offers
        'search_url', // used in success page of instant credit refund
        'selectedCombinationIds', // used in summary and terms page of assisted refund
        'emplifi_survey',
        'emplifi_bid',
      ]),
      // Skip `node_override_api*Url` if `apiBaseOfMmb2` is an empty string.
      ...(apiBaseOfMmb2 && {
        'node_override_api*Url': apiBaseOfMmb2.replace('/mmb/v1/bff/web', ''),
      }),
      ...newQueryParameters,
    },
  };

  return stringifyUrl(pathOfMmb1);
};

/**
 * Used for redirecting back to MMB 1.
 *
 * @returns path to MMB 1 with all queries
 */
export default function usePathToMmb(
  queryParameters: ParsedUrlQuery = {},
  bookingId: number | null = null,
): string {
  const { query, asPath } = useRouter();

  if (!bookingId && !query.bid) {
    throw new Error('bid or bookingId missing');
  }

  return getOptionalPathToMmb(query, queryParameters, asPath, bookingId) as string;
}

export const useOptionalPathToMmb = (
  queryParameters: ParsedUrlQuery = {},
  bookingId: number | null = null,
): string | null => {
  const { query, asPath } = useRouter();

  return getOptionalPathToMmb(query, queryParameters, asPath, bookingId);
};
